/**
 * Components that are not needed to paint the published-site shell. Keep every
 * import literal here: Rolldown uses them as dynamic-entry boundaries and the
 * runtime-manifest plugin reads this registry to make those entries preloadable.
 */
export const runtimeCapabilityLoaders = {
  "taproot-album": () => import("../pages/album/taproot-album.js"),
  "taproot-article": () => import("../pages/article/taproot-article.js"),
  "taproot-byline": () => import("../pages/byline/taproot-byline.js"),
  "taproot-card-grid": () => import("../pages/card-grid/taproot-card-grid.js"),
  "taproot-cta": () => import("../pages/cta/taproot-cta.js"),
  "taproot-feature-grid": () => import("../pages/feature-grid/taproot-feature-grid.js"),
  "taproot-hero": () => import("../pages/hero/taproot-hero.js"),
  "taproot-image-banner": () => import("../pages/image-banner/taproot-image-banner.js"),
  "taproot-latest-posts": () => import("../pages/latest-posts/taproot-latest-posts.js"),
  "taproot-recipe": () => import("../pages/recipe/taproot-recipe.js"),
  "taproot-spacer": () => import("../pages/spacer/taproot-spacer.js"),
  "taproot-testimonial": () => import("../pages/testimonial/taproot-testimonial.js"),
  "esp-checkbox": () => import("./esp-checkbox.js"),
  "esp-checkbox-group": () => import("./esp-checkbox-group.js"),
  "esp-dialog": () => import("./esp-dialog.js"),
  "esp-form-item": () => import("./esp-form-item.js"),
  "esp-lightbox": () => import("./esp-lightbox.js"),
  "esp-popover": () => import("./esp-popover.js"),
  "esp-profile-chip": () => import("./esp-profile-chip.js"),
  "esp-repeater": () => import("./esp-repeater.js"),
  "esp-search": () => import("./esp-search.js"),
  "esp-textarea": () => import("./esp-textarea.js"),
} satisfies Record<string, () => Promise<unknown>>;

export type RuntimeCapabilityTag = keyof typeof runtimeCapabilityLoaders;

const inFlightCapabilities = new Map<RuntimeCapabilityTag, Promise<void>>();

/**
 * Registers the requested custom-element capabilities exactly once. This is
 * deliberately usable after initial reveal for markup introduced by features
 * such as member-content decryption.
 */
export async function loadRuntimeCapabilities(tags: Iterable<string>): Promise<void> {
  const requestedTags = [...new Set(tags)];
  const capabilitiesToLoad = requestedTags.filter((tag) => !isCustomElementDefined(tag));
  for (const tag of capabilitiesToLoad) {
    if (!(tag in runtimeCapabilityLoaders)) {
      throw new Error(`Unknown published-site runtime capability '${tag}'.`);
    }
  }

  await Promise.all(capabilitiesToLoad.map((tag) => loadRuntimeCapability(tag as RuntimeCapabilityTag)));
}

function isCustomElementDefined(tag: string): boolean {
  return globalThis.customElements?.get(tag) !== undefined;
}

function loadRuntimeCapability(tag: RuntimeCapabilityTag): Promise<void> {
  const existing = inFlightCapabilities.get(tag);
  if (existing) return existing;

  const loaded = Promise.resolve()
    .then(runtimeCapabilityLoaders[tag])
    .then(() => undefined);
  inFlightCapabilities.set(tag, loaded);
  void loaded.catch(() => {
    // A transient network failure may succeed on a later explicit request.
    if (inFlightCapabilities.get(tag) === loaded) inFlightCapabilities.delete(tag);
  });
  return loaded;
}
